"use client"

import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import React, { useCallback, useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { iconsMap } from "../../../public/icons/iconsMap"
import { BlockComponent } from "./componentLoader"
import { InvestmentData, TabData } from "./utils"

const MobileSwiperContainer = styled(Box)`
  width: 100%;
  position: relative;
  min-height: 735px;
  .swiper {
    width: 100%;
    height: 100%;
  }
  .swiper-slide {
    width: 100%;
    height: auto;
    opacity: 0;
    transition: opacity 0.3s ease;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    &.swiper-slide-active {
      opacity: 1;
    }
  }
  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
`

const ContentWrapper = styled(Box)`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  background-color: transparent;
`

const FooterSwiper = styled(Box)`
  position: relative;
  bottom: -20px;
  padding-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 10;
  padding: 8px 16px;
  border-radius: 20px;
  min-height: 80px;
  margin-top: auto;
`

const NavigationButton = styled(Box)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background 0.3s ease;
  flex-shrink: 0;
  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
  &.disable {
    opacity: 0.5;
    pointer-events: none;
  }
`

const StyledRulersContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60px;
`

const RotatedLeftArrow = styled(Box)`
  transform: rotate(180deg);
`

const SwiperStyled = styled(Swiper, {
  shouldForwardProp: (prop) => prop !== "isLoading",
})<{ isLoading: boolean }>`
  opacity: ${(props) => (props.isLoading ? 0 : 1)};
`

interface MobileViewProps {
  tabs: TabData[]
  value: number
  onSlideChange: (index: number) => void
  investmentData: InvestmentData
}

const MobileView: React.FC<MobileViewProps> = ({
  tabs,
  value,
  onSlideChange,
  investmentData,
}) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [swiper, setSwiper] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsInitialized(true)
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  // Use useCallback to memoize the slide change handler and prevent setState during render
  const handleSlideChange = useCallback(
    (swiperInstance: any) => {
      // Use setTimeout to defer the state update until after render
      setTimeout(() => {
        onSlideChange(swiperInstance.activeIndex)
      }, 0)
    },
    [onSlideChange],
  )

  const handlePrev = () => {
    if (swiper) {
      swiper.slidePrev()
    }
  }

  const handleNext = () => {
    if (swiper) {
      swiper.slideNext()
    }
  }

  const LeftArrow = iconsMap?.["arrow_blue_white_line"]
  const RightArrow = iconsMap?.["arrow_blue_white_line"]

  return (
    <MobileSwiperContainer>
      <SwiperStyled
        modules={[Navigation]}
        spaceBetween={30}
        slidesPerView={1}
        navigation={false}
        onSlideChange={handleSlideChange}
        initialSlide={isInitialized ? value : 0}
        allowTouchMove={true}
        watchSlidesProgress={true}
        preventInteractionOnTransition={true}
        onSwiper={setSwiper}
        isLoading={isLoading}
        autoHeight={true}
      >
        {tabs?.map((tab, index) => {
          const { rulerTitle, rulerText } = tab || {}
          return (
            <SwiperSlide key={index}>
              <ContentWrapper>
                {tab?.blockToRender && (
                  <BlockComponent
                    blockName={tab.blockToRender}
                    data={tab}
                    investmentData={investmentData}
                  />
                )}
              </ContentWrapper>
              <FooterSwiper>
                <NavigationButton onClick={handlePrev}>
                  <RotatedLeftArrow>
                    {LeftArrow && <LeftArrow />}
                  </RotatedLeftArrow>
                </NavigationButton>
                <StyledRulersContainer>
                  <Typography variant="h4" color="white">
                    {rulerTitle}
                  </Typography>
                  <Typography variant="body2" color="white">
                    {rulerText}
                  </Typography>
                </StyledRulersContainer>
                <NavigationButton onClick={handleNext}>
                  {RightArrow && <RightArrow />}
                </NavigationButton>
              </FooterSwiper>
            </SwiperSlide>
          )
        })}
      </SwiperStyled>
    </MobileSwiperContainer>
  )
}

export default MobileView
